/* ══════════════════════════════════════════════════════════════════════════
   assets/wood-explorer.js — the hub router
   ══════════════════════════════════════════════════════════════════════════
   Hand-authored, and it contains no Liquid and no list of destinations — it
   discovers them from the DOM (see below). So it is cacheable, it is the exact
   code the local preview exercises, and adding a destination never touches it.

   THE ARCHITECTURE, in one sentence: the clearing is a canvas that never
   reloads, and each destination is a layer over it that becomes visible and
   active together, and hidden and dormant together.

   There is no navigation between the clearing and a destination. Opening the
   map does not fetch a page, so the forest photograph behind it is the SAME
   pixels before, during and after — it cannot flash, because it never reloads.

   ── TWO RULES, and where each is actually enforced ───────────────────────

   1. VISIBLE AND ACTIVE ARE SEPARATE SWITCHES.
      A dormant destination is `hidden` AND owns no listeners. The keyboard is
      the sharp end: the map folds on ArrowLeft/ArrowRight, and those keys must
      do nothing whatsoever on the clearing. wood-map.js does not merely ignore
      keys while closed — it has no keydown listener while closed, because the
      listener is added in open() and removed in close().

   2. HEAVY ASSETS LOAD ON OPEN, NOT UP FRONT.
      ensureAssets() below is the mechanism, and it runs once per destination
      for the life of the page.
   ══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var roots = document.querySelectorAll('[data-wx-root]');
  for (var i = 0; i < roots.length; i++) bind(roots[i]);

  function bind(root) {
    if (root.dataset.wxBound) return;
    root.dataset.wxBound = '1';

    /* ── DESTINATIONS DISCOVER THEMSELVES ────────────────────────────────
       Each destination is its own SECTION, and its layer carries everything
       the router needs to open it:

         data-wx-dest  the id           data-wx-api  the global it publishes
         data-wx-css   its stylesheet   data-wx-js   its script

       So there is no registry. Adding a destination is adding one section file
       and dropping it into the template — nothing here changes, and there is no
       second list to forget to update.

       Scanned DOCUMENT-WIDE on purpose: sibling sections render outside this
       section's root, so scoping the query to `root` would find none of them. */
    var DESTINATIONS = {};
    var layers = document.querySelectorAll('[data-wx-dest]');
    for (var n = 0; n < layers.length; n++) {
      var el = layers[n];
      var id = el.getAttribute('data-wx-dest');
      if (!id) continue;
      DESTINATIONS[id] = {
        el:  el,
        api: el.getAttribute('data-wx-api'),
        css: el.getAttribute('data-wx-css'),
        js:  el.getAttribute('data-wx-js')
      };
    }

    var copy    = root.querySelector('[data-wx-copy]');
    var current = null;   // id of the open destination, or null on the clearing
    var loading = null;   // in-flight open, so a double-tap cannot open twice
    var pushedAny = false; // have we ever pushed? decides how the exit leaves
    var exitAfterClose = null; // set when a destination was entered COLD; see open()
    var loaded  = {};     // id -> Promise, resolved once its assets are in

    /* ── ASSET LOADING ──
       The stylesheet is awaited before the script runs, and both are awaited
       before the destination is told to open. Opening over an unapplied
       stylesheet would show the map's raw markup for a frame — which, given
       the first thing the choreography does is fade a packet up out of
       darkness, is precisely the flash this architecture exists to avoid. */
    function loadCSS(href) {
      return new Promise(function (resolve, reject) {
        var el = document.createElement('link');
        el.rel = 'stylesheet';
        el.href = href;
        el.onload = resolve;
        el.onerror = function () { reject(new Error('stylesheet failed: ' + href)); };
        document.head.appendChild(el);
      });
    }
    function loadJS(src) {
      return new Promise(function (resolve, reject) {
        var el = document.createElement('script');
        el.src = src;
        el.async = false;
        el.onload = resolve;
        el.onerror = function () { reject(new Error('script failed: ' + src)); };
        document.head.appendChild(el);
      });
    }
    function ensureAssets(id) {
      if (loaded[id]) return loaded[id];
      var d = DESTINATIONS[id];
      loaded[id] = loadCSS(d.css).then(function () { return loadJS(d.js); });
      return loaded[id];
    }

    /* ── OPEN ──
       `wood` is the deep link: with it, the named marker lands last and
       brightest and its drawer opens once the paper is flat. Without it, a
       neutral reveal. */
    function open(id, opts) {
      opts = opts || {};
      if (current === id || loading) return Promise.resolve();
      var d = DESTINATIONS[id];
      if (!d) return Promise.resolve();

      var layer = d.el;
      if (!layer) return Promise.resolve();

      /* One state change starts BOTH the words leaving and the screen
         deepening, so they are genuinely simultaneous rather than two things
         scheduled to look simultaneous. Set before the assets are awaited: the
         fade should begin the instant the plank is tapped, not when the
         network happens to finish. */
      loading = id;
      root.dataset.wxOpen = id;

      return ensureAssets(id).then(function () {
        var api = window[d.api];
        if (!api || typeof api.open !== 'function') {
          throw new Error('destination "' + id + '" published no open()');
        }
        /* Un-hidden BEFORE open() is called, and it has to be in that order:
           the map measures itself on the way up, and a display:none map
           measures zero. */
        layer.hidden = false;
        current = id;
        loading = null;
        /* ⚠ WHERE "BACK" MEANS depends on how they got IN, and only the router
           knows. Tapping the plank on the signpost means back = the clearing.
           Arriving straight into the map — a swatch in a product page's
           materials section links in as ?wood=padauk — means the visitor has
           never SEEN the clearing, so sending them there is not going back, it
           is sending them somewhere new.

           So the destination is told where its own exit leads, rather than
           assuming. It is passed per-open and not stored as a flag, because a
           visitor can cold-enter, close to the clearing, and then re-open from
           the signpost — at which point back genuinely does mean the clearing
           again, and a flag would have to be un-set at exactly the right
           moment. The call site knows; nothing else has to. */
        exitAfterClose = opts.exitTo || null;
        api.open({
          wood: opts.wood || null,
          exitTo: opts.exitTo || null,
          /* Called when the destination STARTS leaving, not when it has gone.
             The words and the veil come back while the packet is still
             sweeping off, so the two movements overlap instead of queueing —
             the clearing is already returning as the map clears the frame. */
          onLeaving: function () { delete root.dataset.wxOpen; },
          onClosed:  function () { finishClose(id); }
        });
        if (opts.push !== false) { pushURL(id, opts.wood); pushedAny = true; }
      }).catch(function (err) {
        /* Put the clearing back exactly as it was. A destination that fails to
           load must cost the visitor nothing but the copy blinking. */
        loading = null;
        delete root.dataset.wxOpen;      // words back, veil lifts
        if (window.console) console.error('[wood-explorer]', err);
      });
    }

    /* ── CLOSE ──
       Asks the destination to run its own exit choreography. The layer is NOT
       hidden here — the map folds up and flies back down the path first, and
       hiding it now would cut that off. finishClose() is the callback. */
    function close(opts) {
      opts = opts || {};
      if (!current) return;
      var api = window[DESTINATIONS[current].api];
      if (api && typeof api.close === 'function') api.close();
      else finishClose(current);
    }

    function finishClose(id) {
      var layer = DESTINATIONS[id] && DESTINATIONS[id].el;
      if (layer) layer.hidden = true;     // hidden and dormant, together
      if (current === id) current = null;

      /* Cleared HERE, at the end of the exit choreography, not when close() is
         called. The veil has to stay down while the map folds up and flies back
         off — lifting it early would brighten the forest under a map that is
         still leaving. The words come back as the dark lifts. */
      delete root.dataset.wxOpen;

      /* ── LEAVING FOR REAL, IF THAT IS WHERE BACK LED ──
         A cold-entered destination's Back means the page they came from, not
         the clearing. It navigates HERE, at the end of the exit choreography,
         so the map still folds up and flies off first — the visitor sees the
         experience close, then the page changes, rather than the page vanishing
         mid-animation.

         history.back() rather than an assignment when nothing has been pushed:
         the previous entry IS the product page, so this returns them to their
         PLACE on it. A cold entry does a replaceState, not a push, so the
         common case genuinely is depth zero. */
      if (exitAfterClose) {
        var target = exitAfterClose; exitAfterClose = null;
        if (!pushedAny) { history.back(); return; }
        window.location.assign(target.href); return;
      }

      /* A destination can close WITHOUT close() ever running — the map's own
         Back link calls its exit choreography directly. So the URL is
         reconciled here, at the one point every close path passes through, and
         only if it still names a destination. Correct without pushing twice. */
      try {
        var u = new URL(window.location.href);
        if (u.searchParams.has('destination') || u.searchParams.has('wood')) pushURL(null, null);
      } catch (e) { /* the layer is closed either way */ }
    }

    /* ── URL STATE ──
       pushState, not navigation: the canvas must not reload. The payoff is
       that the browser's own Back button closes the destination — the gesture
       people reach for first — and that a deep link is shareable. */
    function pushURL(id, wood) {
      if (!window.history || !history.pushState) return;
      try {
        var url = new URL(window.location.href);
        if (id) {
          url.searchParams.set('destination', id);
          if (wood) url.searchParams.set('wood', wood);
          else url.searchParams.delete('wood');
        } else {
          url.searchParams.delete('destination');
          url.searchParams.delete('wood');
        }
        history.pushState({ wx: id || null, wood: wood || null }, '', url.toString());
      } catch (e) { /* history unavailable — navigation still never happens */ }
    }

    window.addEventListener('popstate', function (ev) {
      var want = (ev.state && ev.state.wx) || null;
      if (want === current) return;
      if (want) open(want, { wood: (ev.state && ev.state.wood) || null, push: false });
      else close({ push: false });
    });

    /* ── LEAVING THE WOODS ──
       The exit from the hub itself, one level up from a destination's Back.

       ⚠ THE REFERRER IS CAPTURED ONCE, AT LOAD, AND IT SURVIVES EVERYTHING.
       This page never reloads — destinations are layers — so document.referrer
       still names the product page a visitor deep-linked from, however much
       map they opened and closed in between. That is the whole reason someone
       who arrives from a product page, wanders into the map, comes back up to
       the clearing and then leaves can be returned to the product page rather
       than dumped on the shop homepage.

       ⚠ WHY NOT ALWAYS history.back(). The two close paths leave the history
       stack at different depths: opening pushes, and finishClose() pushes
       AGAIN to reconcile the URL, so a visitor who used the map's own Back is
       two entries deep — while one who used the browser's Back, or who never
       opened anything, is at zero. A blind history.back() would therefore land
       somewhere different depending on a choice the visitor does not know they
       made.

       So: if we have never pushed, the previous entry IS where they came from,
       and history.back() returns them to their PLACE on it — scroll position
       and all, which matters on a long collection page. If we have pushed, a
       plain assignment goes to the right page and accepts landing at the top
       of it. Never wrong; nicest outcome in the common case.

       One boolean, checked at click time. Counting pushes instead would be
       fragile, because popstate does not report direction and the visitor can
       use the browser's own controls at any point. */
    var origin = (function () {
      try {
        if (!document.referrer) return null;
        var r = new URL(document.referrer);
        if (r.origin !== window.location.origin) return null;   // ad, search, another site
        if (r.pathname === window.location.pathname) return null; // a reload of this page
        return r.href;
      } catch (e) { return null; }
    })();

    var leave = root.querySelector('[data-wx-leave]');
    if (leave) {
      /* A real href either way, so hover, middle-click and cmd-click are
         honest. The rendered fallback is the shop; this only overrides it when
         there is a genuine same-origin page to go back to. */
      if (origin) { try { leave.href = origin; } catch (e) {} }

      leave.addEventListener('click', function (ev) {
        /* Never intercept a modified click — that is the visitor deliberately
           asking for a new tab, and the href is already correct. */
        if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button !== 0) return;
        if (!origin) return;              // no same-origin history; let the href do its job
        ev.preventDefault();
        if (!pushedAny) history.back();   // back to their PLACE on the page they came from
        else window.location.assign(origin);
      });
    }

    /* ── THE SIGNPOST ── delegated, so hotspots can be added without rebinding */
    root.addEventListener('click', function (ev) {
      var hot = ev.target.closest ? ev.target.closest('[data-wx-open]') : null;
      if (!hot) return;
      ev.preventDefault();
      open(hot.getAttribute('data-wx-open'), {});
    });

    /* ── DEEP LINK ON ARRIVAL ──
       ?wood=padauk is the contract the collection banners link on, so it has
       to work as a COLD ENTRY and not only as an in-page transition. `?wood=`
       alone implies the map; `?destination=` names a layer without focusing
       anything inside it. Opened without a pushState — the URL is already
       right, and pushing here would wedge an extra history entry between the
       visitor and wherever they came from. */
    var qs = new URLSearchParams(window.location.search);
    var wantWood = qs.get('wood');
    var wantDest = qs.get('destination') || (wantWood ? 'world-map' : null);
    if (wantDest && DESTINATIONS[wantDest]) {
      if (history.replaceState) {
        history.replaceState({ wx: wantDest, wood: wantWood }, '', window.location.href);
      }
      open(wantDest, {
        wood: wantWood,
        push: false,
        /* COLD ENTRY. They arrived straight into the destination — from a
           product page's materials swatch, a collection banner, an ad — and
           have never seen the clearing. Back therefore means the page they
           came from. With no same-origin referrer (typed URL, search result,
           off-site ad) this stays null and Back falls through to its default,
           the clearing, which is the right place to land someone who has
           nowhere else to be. */
        exitTo: origin ? { href: origin, label: 'back to the shop' } : null
      });
    }

    /* Published for the destinations and for console testing. */
    window.WoodExplorer = {
      open: open,
      close: close,
      current: function () { return current; },
      destinations: Object.keys(DESTINATIONS)
    };
  }
})();
